/**
 * Loads a photo into a small working canvas and computes cheap, model-free metrics on it :
 *  - dHash (64 bits) used to find exact and near duplicates (bursts)
 *  - variance of the Laplacian : sharpness, low values mean a blurry picture
 *  - mean luminance and clipped ratios : under / over exposure
 *  - colorfulness (Hasler & Süsstrunk) : documents and screenshots are almost colorless
 *
 * Everything runs on the main thread, on thumbnails (256 px), and takes a few ms per photo.
 * The canvas is kept by the caller : the face finder and the smart cropper reuse it.
 */
fx.ns("curator", class ImageAnalyzer {

    constructor(options)
    {
        options = options || {};

        this.size = options.size || 256;                 // long side of the working canvas
        this.loadTimeout = options.loadTimeout || 15000;  // ms
    }

    /**
     * Resolves with a canvas holding the photo scaled to this.size on its long side.
     * Rejects when the photo has no usable source, cannot be loaded, or taints the canvas (no CORS).
     */
    load(photo)
    {
        let src = this.source(photo);

        if (!src)
            return Promise.reject(new Error("no-source"));

        return this._loadImage(src).then((img) => this._toCanvas(img));
    }

    /**
     * Best available source : the base64 thumb made at extraction (no network, no CORS), then the
     * thumb url (server side thumb once uploaded), then the light url.
     */
    source(photo)
    {
        if (photo.base64 && /^data:/.test(photo.base64))
            return photo.base64;

        let placeholder = (fx.assets && fx.assets.imagePlaceHolder) ? fx.assets.imagePlaceHolder.content : null;

        if (photo.thumb && photo.thumb != placeholder)
            return photo.thumb;

        return photo.light_url || photo.url || null;
    }

    /**
     * Metrics of a canvas : { width, height, hash, sharpness, brightness, clippedDark, clippedBright, colorfulness }
     */
    analyse(canvas)
    {
        let ctx = canvas.getContext("2d", { willReadFrequently : true });
        let width = canvas.width, height = canvas.height;
        let data = ctx.getImageData(0, 0, width, height).data;
        let gray = new Float32Array(width * height);

        let n = width * height;
        let sumY = 0, dark = 0, bright = 0;
        let sumRg = 0, sumYb = 0, sumRg2 = 0, sumYb2 = 0;

        for (let i = 0, p = 0; i < n; i++, p += 4)
        {
            let r = data[p], g = data[p + 1], b = data[p + 2];
            let y = 0.299 * r + 0.587 * g + 0.114 * b;

            gray[i] = y;
            sumY += y;

            if (y < 10)
                dark++;
            else if (y > 245)
                bright++;

            let rg = r - g, yb = (r + g) / 2 - b;
            sumRg += rg;
            sumYb += yb;
            sumRg2 += rg * rg;
            sumYb2 += yb * yb;
        }

        let meanRg = sumRg / n, meanYb = sumYb / n;
        let stdRg = Math.sqrt(Math.max(0, sumRg2 / n - meanRg * meanRg));
        let stdYb = Math.sqrt(Math.max(0, sumYb2 / n - meanYb * meanYb));

        return {
            width : width,
            height : height,
            hash : this._dHash(gray, width, height),
            sharpness : this._laplacianVariance(gray, width, height),
            brightness : sumY / n / 255,
            clippedDark : dark / n,
            clippedBright : bright / n,
            colorfulness : Math.sqrt(stdRg * stdRg + stdYb * stdYb) + 0.3 * Math.sqrt(meanRg * meanRg + meanYb * meanYb)
        };
    }

    /** Number of differing bits between two hashes (0 = identical, 64 = nothing in common). */
    static hammingDistance(a, b)
    {
        if (!a || !b)
            return 64;

        return ImageAnalyzer._popcount(a[0] ^ b[0]) + ImageAnalyzer._popcount(a[1] ^ b[1]);
    }

    static _popcount(v)
    {
        v = v - ((v >>> 1) & 0x55555555);
        v = (v & 0x33333333) + ((v >>> 2) & 0x33333333);
        return Math.imul((v + (v >>> 4)) & 0x0F0F0F0F, 0x01010101) >>> 24;
    }

    // ------------------------------------------------------------------ //

    /**
     * Difference hash : the picture is reduced to a 9x8 grid of box averages, each bit tells whether
     * a cell is darker than its right neighbour. Robust to resizing, recompression and small edits.
     * Returned as two unsigned 32 bit words.
     */
    _dHash(gray, width, height)
    {
        let cells = new Float32Array(72);

        for (let row = 0; row < 8; row++)
        {
            let y0 = Math.floor(row * height / 8);
            let y1 = Math.max(y0 + 1, Math.floor((row + 1) * height / 8));

            for (let col = 0; col < 9; col++)
            {
                let x0 = Math.floor(col * width / 9);
                let x1 = Math.max(x0 + 1, Math.floor((col + 1) * width / 9));

                let sum = 0, count = 0;
                for (let y = y0; y < y1 && y < height; y++)
                    for (let x = x0; x < x1 && x < width; x++)
                    {
                        sum += gray[y * width + x];
                        count++;
                    }

                cells[row * 9 + col] = count ? sum / count : 0;
            }
        }

        let hi = 0, lo = 0, bit = 0;
        for (let row = 0; row < 8; row++)
            for (let col = 0; col < 8; col++, bit++)
            {
                if (cells[row * 9 + col] < cells[row * 9 + col + 1])
                {
                    if (bit < 32)
                        hi |= (1 << bit);
                    else
                        lo |= (1 << (bit - 32));
                }
            }

        return [hi >>> 0, lo >>> 0];
    }

    _laplacianVariance(gray, width, height)
    {
        let sum = 0, sum2 = 0, n = 0;

        for (let y = 1; y < height - 1; y++)
            for (let x = 1; x < width - 1; x++)
            {
                let i = y * width + x;
                let v = gray[i - 1] + gray[i + 1] + gray[i - width] + gray[i + width] - 4 * gray[i];
                sum += v;
                sum2 += v * v;
                n++;
            }

        if (!n)
            return 0;

        let mean = sum / n;
        return Math.max(0, sum2 / n - mean * mean);
    }

    _loadImage(src)
    {
        return new Promise((resolve, reject) => {
            let img = new Image();

            let timer = setTimeout(() => {
                img.onload = img.onerror = null;
                reject(new Error("timeout"));
            }, this.loadTimeout);

            // data: and blob: urls are same origin ; anything else needs CORS to be readable
            if (!/^(data|blob):/i.test(src))
                img.crossOrigin = "anonymous";

            img.onload = () => { clearTimeout(timer); resolve(img); };
            img.onerror = () => { clearTimeout(timer); reject(new Error("load-error")); };
            img.src = src;
        });
    }

    _toCanvas(img)
    {
        let w = img.naturalWidth || img.width, h = img.naturalHeight || img.height;

        if (!w || !h)
            throw new Error("empty-image");

        let scale = Math.min(1, this.size / Math.max(w, h));
        let canvas = document.createElement("canvas");
        canvas.width = Math.max(1, Math.round(w * scale));
        canvas.height = Math.max(1, Math.round(h * scale));

        let ctx = canvas.getContext("2d", { willReadFrequently : true });
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        // a tainted canvas throws here : better now than in every consumer
        ctx.getImageData(0, 0, 1, 1);

        return canvas;
    }
});
